import {
  Breadcrumbs,
  Card,
  Typography,
  Grid,
  Input,
  Stack,
  FormLabel,
  Box,
  CircularProgress,
} from '@mui/joy';
import { AxiosError } from 'axios';
import { CreateEditUserModal } from '@/components/Pages/Users/CreateEditUserModal';
import { createFileRoute, Link } from '@tanstack/react-router';
import { DeleteUserModal } from '@/components/Pages/Users/DeleteUserModal';
import { SellTable } from '@/components/Pages/Sells/SellTable';
import { useQuery } from '@tanstack/react-query';
import { UserResponseDTO, QueryResponse } from '@/types';
import api from '@/services/axios';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/users/$id')({
  component: UserDetail,
});

function UserDetail() {
  const { id } = Route.useParams();
  const { isAdmin, isSuperAdmin } = useAuth();
  const showActions = isAdmin || isSuperAdmin;

  const { data: user, isLoading } = useQuery<
    QueryResponse<UserResponseDTO>,
    AxiosError,
    UserResponseDTO
  >({
    queryKey: ['users', id],
    queryFn: () => api.get(`/user/${id}`),
    select: ({ data }) => data.data,
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <div>Usuario no encontrado</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction='row' justifyContent='space-between'>
        <Breadcrumbs size='sm'>
          <Link to='/users'>Usuarios</Link>
          <Typography>{user?.fullName}</Typography>
        </Breadcrumbs>

        {showActions && (
          <Stack direction='row' gap={1}>
            <CreateEditUserModal user={user} />
            <DeleteUserModal id={user?.id.toString()} name={user?.fullName} />
          </Stack>
        )}
      </Stack>

      <Card>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid xs={12} md={6}>
            <FormLabel>Nombre</FormLabel>
            <Input
              value={user?.firstName}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Apellido</FormLabel>
            <Input
              value={user?.lastName}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Email</FormLabel>
            <Input
              value={user?.email}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Teléfono</FormLabel>
            <Input
              value={user?.phoneNumber}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>DNI</FormLabel>
            <Input
              value={user?.dni?.toString()}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Rol</FormLabel>
            <Input
              value={user?.roleName}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Comisión</FormLabel>
            <Input
              value={`${user?.commission || 0}%`}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ mt: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography level='h4'>Ventas del usuario</Typography>
        </Box>

        <SellTable
          sellerId={user.id}
          showSellerColumn={false}
          showFilters={false}
          emptyMessage='No hay ventas para este usuario'
          errorTitle='Error al cargar las ventas del usuario'
        />
      </Card>
    </Box>
  );
}
